// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { CsvImport } from './CsvImport.js';
import { QifImport } from './QifImport.js';
import { OfxImport } from './OfxImport.js';
import { CoinbaseImport } from './CoinbaseImport.js';

interface Props {
  worker: Worker | null;
  keyData: ArrayBuffer | null;
}

type Format = 'csv' | 'qif' | 'ofx' | 'coinbase' | null;

const FORMAT_CARDS = [
  { id: 'csv' as const, abbr: 'CSV' },
  { id: 'qif' as const, abbr: 'QIF' },
  { id: 'ofx' as const, abbr: 'OFX' },
  { id: 'coinbase' as const, abbr: 'CB' },
];

export function ImportPage({ worker, keyData }: Props) {
  const { t } = useTranslation(['import', 'common', 'errors']);
  const [searchParams] = useSearchParams();
  const preselectedAccountId = searchParams.get('accountId') ?? undefined;

  const [format, setFormat] = useState<Format>(null);

  const breadcrumbCls = 'flex items-center gap-2 mb-6';
  const backBtnCls = 'text-sm text-accent hover:underline';

  if (format === 'csv') {
    return (
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-3xl mx-auto">
          <div className={breadcrumbCls}>
            <button onClick={() => setFormat(null)} className={backBtnCls}>← {t('common:nav.import')}</button>
            <span className="text-text-subtle">/</span>
            <h1 className="text-md font-semibold text-text">{t('page.importCsv')}</h1>
          </div>
          <CsvImport worker={worker} keyData={keyData} {...(preselectedAccountId !== undefined ? { preselectedAccountId } : {})} />
        </div>
      </div>
    );
  }

  if (format === 'qif') {
    return (
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-3xl mx-auto">
          <div className={breadcrumbCls}>
            <button onClick={() => setFormat(null)} className={backBtnCls}>← {t('common:nav.import')}</button>
            <span className="text-text-subtle">/</span>
            <h1 className="text-md font-semibold text-text">{t('page.importQif')}</h1>
          </div>
          <QifImport worker={worker} keyData={keyData} {...(preselectedAccountId !== undefined ? { preselectedAccountId } : {})} />
        </div>
      </div>
    );
  }

  if (format === 'ofx') {
    return (
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-3xl mx-auto">
          <div className={breadcrumbCls}>
            <button onClick={() => setFormat(null)} className={backBtnCls}>← {t('common:nav.import')}</button>
            <span className="text-text-subtle">/</span>
            <h1 className="text-md font-semibold text-text">{t('page.importOfx')}</h1>
          </div>
          <OfxImport worker={worker} keyData={keyData} {...(preselectedAccountId !== undefined ? { preselectedAccountId } : {})} />
        </div>
      </div>
    );
  }

  if (format === 'coinbase') {
    return (
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-3xl mx-auto">
          <div className={breadcrumbCls}>
            <button onClick={() => setFormat(null)} className={backBtnCls}>← {t('common:nav.import')}</button>
            <span className="text-text-subtle">/</span>
            <h1 className="text-md font-semibold text-text">{t('page.importCoinbase')}</h1>
          </div>
          <CoinbaseImport worker={worker} keyData={keyData} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto p-6">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-md font-bold text-text mb-2">{t('page.title')}</h1>
        <p className="text-text-muted text-sm mb-6">
          {t('page.intro')}
        </p>

        <div className="grid grid-cols-4 gap-3 mb-8">
          {FORMAT_CARDS.map((card) => (
            <button
              key={card.id}
              onClick={() => setFormat(card.id)}
              className="text-left p-4 border border-border hover:border-accent hover:bg-bg-elevated transition-colors"
            >
              <div className="font-mono text-lg font-semibold text-accent mb-2">{card.abbr}</div>
              <div className="font-medium text-text text-sm mb-1">{t(`page.formats.${card.id}.name`)}</div>
              <div className="text-xs text-text-muted mb-2">{t(`page.formats.${card.id}.description`)}</div>
              <div className="text-xs text-text-subtle">{t(`page.formats.${card.id}.supportedBy`)}</div>
            </button>
          ))}
        </div>

        <div className="p-4 bg-bg-surface border border-border text-sm text-text-muted">
          <p className="font-medium text-text mb-2 text-xs uppercase tracking-wide">{t('page.tips')}</p>
          <ul className="space-y-1 text-text-muted">
            <li>{t('page.tipOfx')}</li>
            <li>{t('page.tipDuplicate')}</li>
            <li>{t('page.tipReview')}</li>
            <li>{t('page.tipNonDestructive')}</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
